import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateFieldBindings } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import {
	useTranslationDefault,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { FieldBindingDto } from '@dealstack/frontend/shared/components/FieldBinding/fieldBinding.types.ts';
import { buildFieldBindingsQueryKey } from './useFieldBindingsQuery.ts';

interface Params {
	templateId: string;
	bindings: FieldBindingDto[];
}

export const useUpdateFieldBindings = () => {
	const tMipManage = useTranslationMipManage();
	const tDefault = useTranslationDefault();
	const queryClient = useQueryClient();
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');

	return useMutation({
		mutationFn: async (params: Params): Promise<void> => {
			if (!params.templateId) {
				throw new Error('Template ID not found');
			}
			await updateFieldBindings({
				dealId,
				programmeId: mipId,
				templateId: params.templateId,
				bindings: params.bindings,
			});
		},
		onSuccess: async (_, { templateId }) => {
			await queryClient.invalidateQueries({
				queryKey: [
					buildFieldBindingsQueryKey({
						dealId,
						programmeId: mipId,
						templateId,
					}),
				],
			});
			showToastV2({
				title: tMipManage('master_documents.toast.updated'),
			});
		},
		onError: (error: unknown) => {
			if (isStandardBackendError(error)) {
				showToastV2({
					state: 'error',
					title: tDefault(`error_codes.${error.error?.code}`),
				});
				return;
			}
			const backendMessage = (error as { error?: { message?: string } })?.error?.message;
			if (typeof backendMessage === 'string' && backendMessage.length > 0) {
				showToastV2({
					state: 'error',
					title: backendMessage,
				});
				return;
			}
			showToastV2({
				state: 'error',
				title: tDefault('common.toast.error.message'),
			});
		},
	});
};
